import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import PropTypes from 'prop-types'
import { Formik, Field, Form, FieldArray } from 'formik'
import yup from 'yup'
import map from 'lodash/map'
import find from 'lodash/find'
import { BigNumber } from 'bignumber.js'

import { precision } from '@atheneum/api/dist/lib'

import Button from '@components/Button'
import { Dropdown } from '@components/Form/Autocomplete'
import Input from '@components/Form/Input'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'

const NO_CONTRACT = { id: null, label: 'None' }

class BatchContractsAssign extends Component {
  static defaultProps = {
    hideButtons: true,
    showCloseIcon: false,
    shouldCloseOnOverlayClick: true
  }

  static propTypes = {
    fees: PropTypes.object.isRequired,
    onOk: PropTypes.func.isRequired,
    contractAssignStore: PropTypes.object.isRequired,
    modalStore: PropTypes.object.isRequired,
    staticData: PropTypes.object.isRequired
  }
  constructor (props) {
    super(props)
    props.contractAssignStore.init(props.fees)
  }

  onContractChange = async (form, contract) => {
    form.setFieldValue('contract', contract, false)

    const newData = await this.props.contractAssignStore.recalculateFees(
      form.values.fees,
      contract
    )

    map(newData, (fee, index) => {
      ;['feeValue', 'currencyId', 'credits', 'PAYG'].forEach(key => {
        form.setFieldValue(`fees[${index}].${key}`, fee[key], false)
        form.setFieldTouched(`fees[${index}].${key}`, true)
      })
    })
  }

  getValidationSchema () {
    const fee = yup.object().shape({
      PAYG: yup.boolean(),
      credits: yup
        .number()
        .nullable()
        .when('PAYG', (PAYG, schema) => {
          if (!PAYG) {
            return schema.required(
              'Credits are required for PAC and SUB contracts'
            )
          } else {
            return schema
          }
        })
        .typeError('Credits must be a number.')
        .test(
          'two-decimal-places',
          'Number can contain only 2 decimal places. (e.g. 33.20)',
          precision(2)
        ),
      feeValue: yup
        .number()
        .nullable()
        .when('PAYG', (PAYG, schema) => {
          if (PAYG) {
            return schema.required('Fee value is required for PAYG contracts')
          } else {
            return schema
          }
        })
        .typeError('Fee value must be a number.')
        .test(
          'two-decimal-places',
          'Number can contain only 2 decimal places. (e.g. 33.20)',
          precision(2)
        ),
      currencyId: yup
        .number()
        .integer()
        .nullable()
        .when('PAYG', (PAYG, schema) => {
          if (PAYG) {
            return schema.required(
              'Fee currency is required for PAYG contracts'
            )
          } else {
            return schema
          }
        })
    })

    return yup.object().shape({
      fees: yup.array().of(fee)
    })
  }

  initForm = () => {
    return {
      contract: NO_CONTRACT,
      fees: toJS(this.props.fees).map(fee => {
        return {
          id: fee.id,
          credits: 0,
          currencyId: 1,
          feeValue: 0,
          PAYG: false,
          fee
        }
      })
    }
  }

  assignFees = async values => {
    try {
      await this.props.contractAssignStore.assignFees(values)
      this.props.onOk()
      this.props.modalStore.close()
    } catch (error) {
      this.error = error
    }
  }

  render () {
    const store = this.props.contractAssignStore

    if (!store.ready) return null

    return (
      <div>
        {' '}
        <Formik
          enableReinitialize
          initialValues={this.initForm()}
          validationSchema={this.getValidationSchema()}
          onSubmit={this.assignFees}
          render={form => (
            <Form onSubmit={form.handleSubmit}>
              <div className='left-text'>
                <h2>Assign contract to fees</h2>
                <div className='m-b-lg'>
                  {this.error ? (
                    <InfoBox theme='red' icon={<WarningIcon />}>
                      {this.error}
                    </InfoBox>
                  ) : (
                    <InfoBox>
                      <div>
                        Please check all credits and values changes on fees and
                        edit them as necessary before proceeding.
                      </div>
                    </InfoBox>
                  )}
                </div>
                <div>
                  <Field
                    name='contract'
                    placeholder='Select a contract'
                    label='Contract'
                    onChange={val => this.onContractChange(form, val)}
                    itemToString={item => item && item.label}
                    options={[
                      NO_CONTRACT,
                      ...store.applicableContracts.map(contract => ({
                        ...contract,
                        label: contract.contractReference
                      }))
                    ]}
                    component={Dropdown}
                  />
                </div>
                <FieldArray
                  name='fees'
                  render={arrayHelpers => (
                    <div>
                      {map(
                        form.values.fees.map((fee, index) => (
                          <FeeCard
                            staticData={this.props.staticData}
                            key={index}
                            form={form}
                            index={index}
                            fee={fee}
                            store={store}
                            currencies={this.currencies}
                          />
                        ))
                      )}
                    </div>
                  )}
                />
                <div>
                  <Button primary submit onClick={this.onSubmit}>
                    Assign
                  </Button>
                  <Button onClick={this.props.modalStore.close}>Cancel</Button>
                </div>
              </div>
            </Form>
          )}
        />
      </div>
    )
  }
}

const FeeCard = observer(
  ({ fee, form, index, currencies, store, staticData }) => {
    const feeItem = find(fee.fee.feeItems, feeItem =>
      Boolean(feeItem.feeTypeId)
    )

    let feeType = staticData.getDataFromArray(feeItem.feeTypeId, 'feeTypes')

    return (
      <div className='fee-card' key={index}>
        <div>
          <div className='fee-card__old-values-container'>
            <strong className='text-wrapper m-b-0'>
              <span to={'fee/' + fee.id}>{feeType || '-'}</span>
              {fee.expert ? (
                <span className='desc desc--top-spacing'>
                  {fee.expert.title} {fee.expert.fullName}
                </span>
              ) : null}
            </strong>
            <div>Credits: {feeItem.credits}</div>
            <div>
              Fee value: {feeItem.value}{' '}
              {staticData.names.currencies[feeItem.currencyId]}
            </div>
          </div>
          <div className='fee-card__new-values-container'>
            <Field
              name={`fees[${index}].credits`}
              placeholder='Credits'
              label='Credits'
              extraShort
              onChange={e => {
                form.setFieldValue(
                  `fees[${index}].credits`,
                  e.target.value,
                  false
                )
                form.setFieldTouched(`fees[${index}].credits`, true)

                const testValue = Number(e.target.value)
                if (!Number.isNaN(testValue)) {
                  const feeValue = BigNumber(e.target.value || 0)
                    .times(form.values.contract.creditValue || 0)
                    .toNumber()

                  form.setFieldValue(`fees[${index}].feeValue`, feeValue, false)
                  form.setFieldTouched(`fees[${index}].feeValue`, true)
                }
              }}
              disabled={form.values.fees[index].PAYG}
              component={Input}
            />
            <div className='expert-form__input-group'>
              <label className='expert-form__label'>Fee value</label>
              <div className='expert-form__input-wrapper expert-form__input-wrapper__inline-children'>
                <Field
                  noLabel
                  extraShort
                  name={`fees[${index}].currencyId`}
                  placeholder='Select currency'
                  options={staticData.currencies}
                  itemToString={item => item && item.name}
                  disabled={!form.values.fees[index].PAYG}
                  component={Dropdown}
                />
                <Field
                  name={`fees[${index}].feeValue`}
                  placeholder='Amount'
                  extraShort
                  disabled={!form.values.fees[index].PAYG}
                  component={Input}
                />
                <div />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

export default inject('staticData', 'modalStore', 'contractAssignStore')(
  observer(BatchContractsAssign)
)
